import { useState, useEffect, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";

const STORAGE_KEY = "nil_beauty_favorites";
const SESSION_KEY = "nil_beauty_session_id";

const getSessionId = (): string => {
 let id = sessionStorage.getItem(SESSION_KEY);
 if (!id) {
 id = crypto.randomUUID();
 sessionStorage.setItem(SESSION_KEY, id);
 }
 return id;
};

const getLocalFavorites = (): string[] => {
 try {
 return JSON.parse(localStorage.getItem(STORAGE_KEY) || "[]");
 } catch {
 return [];
 }
};

const setLocalFavorites = (ids: string[]) => {
 localStorage.setItem(STORAGE_KEY, JSON.stringify(ids));
};

export interface FavoriteToggleMeta {
 productName: string;
 variantLabel?: string;
}

const logFavoriteAnalytics = async (
 productId: string,
 action: "add" | "remove",
 meta: FavoriteToggleMeta,
 userId?: string
) => {
 try {
 await supabase.from("favorite_analytics").insert({
 product_id: productId,
 product_name: meta.productName,
 variant_label: meta.variantLabel || null,
 action,
 user_id: userId || null,
 session_id: getSessionId(),
 });
 } catch {
 // Silent fail, analytics should never block UX
 }
};

export const useFavorites = () => {
 const { user } = useAuth();
 const [favorites, setFavorites] = useState<string[]>([]);
 const [loading, setLoading] = useState(true);

 useEffect(() => {
 const load = async () => {
 if (user) {
 const { data } = await supabase
 .from("favorites")
 .select("product_id")
 .eq("user_id", user.id);
 const ids = (data || []).map((r: { product_id: string }) => r.product_id);
 setFavorites(ids);

 const local = getLocalFavorites();
 if (local.length > 0) {
 const toInsert = local.filter((id) => !ids.includes(id));
 if (toInsert.length > 0) {
 await supabase
 .from("favorites")
 .insert(toInsert.map((product_id) => ({ user_id: user.id, product_id })));
 setFavorites([...ids, ...toInsert]);
 }
 localStorage.removeItem(STORAGE_KEY);
 }
 } else {
 setFavorites(getLocalFavorites());
 }
 setLoading(false);
 };
 load();
 }, [user]);

 const isFavorite = useCallback(
 (productId: string) => favorites.includes(productId),
 [favorites]
 );

 const toggleFavorite = useCallback(
 async (productId: string, meta?: FavoriteToggleMeta) => {
 const isFav = favorites.includes(productId);
 const action = isFav ? "remove" : "add";
 const analyticsName = meta?.productName || productId;

 if (isFav) {
 setFavorites((prev) => prev.filter((id) => id !== productId));
 if (user) {
 await supabase
 .from("favorites")
 .delete()
 .eq("user_id", user.id)
 .eq("product_id", productId);
 } else {
 setLocalFavorites(favorites.filter((id) => id !== productId));
 }
 } else {
 setFavorites((prev) => [...prev, productId]);
 if (user) {
 await supabase
 .from("favorites")
 .insert({ user_id: user.id, product_id: productId });
 } else {
 setLocalFavorites([...favorites, productId]);
 }
 }

 // Fire analytics asynchronously
 logFavoriteAnalytics(
 productId,
 action,
 { productName: analyticsName, variantLabel: meta?.variantLabel },
 user?.id
 );
 },
 [favorites, user]
 );

 return { favorites, isFavorite, toggleFavorite, loading };
};
